import { clsx } from 'clsx'
import * as React from 'react'
import { useId } from 'react'
import { Slot } from '@radix-ui/react-slot'

export type TextAreaProps = Omit<
  React.JSX.IntrinsicElements['textarea'],
  'ref'
> & {
  ref?: React.Ref<HTMLTextAreaElement>
  asChild?: boolean
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, asChild, ...rest }, ref) => {
    const Comp = asChild ? Slot : 'textarea'

    return (
      <Comp
        {...rest}
        className={clsx(
          className,
          'w-full resize-none border border-dark-border-solid scroll-pb-[10px] rounded-lg bg-dark-shade px-[14px] py-[10px] text-xs leading-4 text-dark-dim placeholder:text-dark-faint focus-visible:outline-0'
        )}
        ref={ref}
      />
    )
  }
)

type LabeledTextAreaProps = {
  variant?: 'block'
  name: string
  label: React.ReactNode
  textAreaProps: Omit<TextAreaProps, 'name'>
}

export const LabeledInput = React.forwardRef<
  HTMLLabelElement,
  LabeledTextAreaProps
>(({ label, name, variant = 'block', textAreaProps }, ref) => {
  const reactId = useId()
  return (
    <label
      ref={ref}
      htmlFor={textAreaProps.id ?? reactId}
      className={clsx(
        'block',
        variant === 'block' && '-m-2 rounded-xl p-2 hover:bg-dark-shade-hover'
      )}
      draggable={variant === 'block'}
    >
      <span className="block pb-1 text-xs font-semibold select-none text-dark-muted">
        {label}
      </span>
      <TextArea
        id={textAreaProps.id ?? reactId}
        name={name}
        {...textAreaProps}
      />
    </label>
  )
})
